/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import _ from 'lodash';

import { AdresseForm } from '@mairistem/adresses';

import { Checkbox, Form, Grid } from '@mairistem/composants';

import { type Adresse, type Individu, ListeRecensement } from '@/types';

import { formatAdresse, validationAdresse } from '@/utils';
import PanelModalEdition from '../PanelModalEdition';

interface AdresseRattachementModalProps {
  adresseRattachement: Partial<Adresse>;
  isResideCommune: boolean;
  recensement: ListeRecensement;
  updated: boolean;
  onChange: (data: Partial<Individu>) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
  onCancel: () => void;
}

const AdresseRattachementModal = (props: AdresseRattachementModalProps) => {
  const {
    adresseRattachement,
    isResideCommune,
    recensement,
    updated,
    onChange,
    onSave,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Partial<Individu> & { api?: boolean }
  >({});

  const handleAdresseChange = React.useCallback(
    ({ adresse }) => {
      onChange({
        adresseRattachement: formatAdresse(adresseRattachement, adresse),
      });
    },
    [onChange, adresseRattachement],
  );

  const handleChangeResideCommune = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
      onChange({
        isResideCommune: data.checked,
        adresseRattachement: null,
      });
    },
    [onChange],
  );

  const handleSave = React.useCallback(() => {
    const e =
      recensement !== ListeRecensement.NonRecenses
        ? validationAdresse(adresseRattachement)
        : {};

    setErrors(e);

    if (_.isEmpty(e)) {
      setLoading(true);

      onSave()
        .then(() => {
          onClose();
        })
        .catch(() => {
          setErrors({ api: true });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [adresseRattachement, recensement, onClose, onSave]);

  return (
    <PanelModalEdition
      errors={errors}
      loading={loading}
      header="Adresse de rattachement"
      updated={updated}
      onSave={handleSave}
      onClose={onCancel}
      size="large"
    >
      <Grid as={Form}>
        <Grid.Row style={{ paddingBottom: 0 }}>
          <Grid.Column width={16} style={{ padding: '0 1em' }}>
            <Checkbox
              toggle
              name="resideCommune"
              label="Réside dans la commune"
              data-testid="fiche-panel-recensement-modal-checkbox-reside-commune"
              checked={isResideCommune}
              onChange={handleChangeResideCommune}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <AdresseForm
              saisie="libre"
              displayDepartement={false}
              adresse={{
                ...adresseRattachement,
                type: isResideCommune ? 'Commune' : 'Hors Commune',
                codePostal: adresseRattachement?.libelleCodePostal,
                commune: adresseRattachement?.libelleCommune,
                voie: adresseRattachement?.libelleVoie,
                pays: adresseRattachement?.libellePays,
              }}
              data-testid="contact-adress"
              onItemChange={handleAdresseChange}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </PanelModalEdition>
  );
};

export default AdresseRattachementModal;
